import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["New", "In Discussion", "Quoted", "Contracted"];
const CATEGORIES = ["Residential", "Renovation", "Commercial"];
const inputCls = "w-full bg-charcoal border border-white/10 px-4 py-3 text-sm text-cream placeholder:text-stone-600 focus:outline-none focus:border-bronze transition-colors";

const STATUS_COLORS = {
  "New": "text-cedar border-bronze/50",
  "In Discussion": "text-sky-300 border-sky-400/40",
  "Quoted": "text-amber-200 border-amber-300/40",
  "Contracted": "text-emerald-300 border-emerald-400/40",
};

function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((en) => (en.id === id ? { ...en, status } : en)));
      toast.success(`Status updated to ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((en) => en.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shown = enquiries.filter((en) =>
    `${en.name} ${en.email} ${en.project_type} ${en.location}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="enquiries-panel">
      <input data-testid="enquiry-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search name, email, type, location…" className={`${inputCls} mb-6 max-w-md`} />
      {shown.length === 0 && (
        <p data-testid="enquiries-empty" className="text-stone-500 text-sm py-16 text-center">No enquiries yet.</p>
      )}
      <div className="space-y-4">
        {shown.map((en) => (
          <article key={en.id} data-testid={`enquiry-card-${en.id}`} className="bg-surface border border-white/[0.08] p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-sans text-lg font-semibold text-cream">{en.name}</h3>
                <p className="text-sm text-stone-400 mt-0.5">{en.email}{en.phone ? ` · ${en.phone}` : ""}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bronze mt-2">
                  {en.project_type} · {en.location || "—"} · {en.budget || "—"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select data-testid={`enquiry-status-select-${en.id}`} value={en.status}
                  onChange={(e) => setStatus(en.id, e.target.value)}
                  className={`bg-charcoal border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.15em] focus:outline-none ${STATUS_COLORS[en.status] || "text-stone-300 border-white/10"}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`enquiry-delete-${en.id}`} onClick={() => remove(en.id)} aria-label="Delete enquiry"
                  className="p-2.5 border border-white/10 text-stone-500 hover:text-red-400 hover:border-red-400/50 transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <p className="mt-4 text-sm text-stone-400 font-light leading-relaxed border-t border-white/[0.06] pt-4">{en.message}</p>
            <p className="mt-3 font-mono text-[10px] text-stone-600 tracking-widest">
              {new Date(en.created_at).toLocaleString("en-NZ")}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}

function Projects() {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Residential", location: "", year: String(new Date().getFullYear()), image_url: "", description: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", form);
      setProjects((list) => [data, ...list]);
      setShowForm(false);
      setForm({ title: "", category: "Residential", location: "", year: String(new Date().getFullYear()), image_url: "", description: "" });
      toast.success("Project added to portfolio");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((list) => list.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="projects-panel">
      <button data-testid="project-add-toggle" onClick={() => setShowForm(!showForm)}
        className="mb-6 flex items-center gap-2 px-5 py-3 bg-bronze text-obsidian font-semibold text-sm hover:bg-cedar transition-colors">
        <Plus size={16} /> Add Project
      </button>
      {showForm && (
        <form data-testid="project-add-form" onSubmit={add}
          className="mb-8 bg-surface border border-white/[0.08] p-6 grid sm:grid-cols-2 gap-4">
          <input data-testid="project-title-input" required placeholder="Project title" value={form.title} onChange={set("title")} className={inputCls} />
          <select data-testid="project-category-select" value={form.category} onChange={set("category")} className={inputCls}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="project-location-input" required placeholder="Location (e.g. Greytown)" value={form.location} onChange={set("location")} className={inputCls} />
          <input data-testid="project-year-input" required placeholder="Year" value={form.year} onChange={set("year")} className={inputCls} />
          <input data-testid="project-image-input" required type="url" placeholder="Image URL (https://…)" value={form.image_url} onChange={set("image_url")} className={`${inputCls} sm:col-span-2`} />
          <textarea data-testid="project-description-input" required rows={3} placeholder="Project description" value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2 resize-none`} />
          <button data-testid="project-submit-btn" type="submit"
            className="sm:col-span-2 py-3 bg-bronze text-obsidian font-semibold text-sm hover:bg-cedar transition-colors">
            Save Project
          </button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p) => (
          <article key={p.id} data-testid={`admin-project-card-${p.id}`} className="bg-surface border border-white/[0.08] overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bronze">{p.category} · {p.year}</span>
              <h3 className="font-serif text-xl text-cream mt-1">{p.title}</h3>
              <p className="text-xs text-stone-500 mt-1">{p.location}</p>
              <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p.id)}
                className="mt-4 flex items-center gap-2 text-xs text-stone-500 hover:text-red-400 transition-colors">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");

  const signOut = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-charcoal/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand" className="font-serif text-xl text-cream">
            LW<span className="text-bronze">&amp;</span>CO <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 ml-2">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500" data-testid="admin-user-email">{user?.email}</span>
            <button data-testid="admin-logout-btn" onClick={signOut}
              className="flex items-center gap-2 text-sm text-stone-400 hover:text-cedar transition-colors">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <div className="flex gap-2 mb-10" data-testid="admin-tabs">
          {[["enquiries", "Enquiries"], ["projects", "Portfolio Projects"]].map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] border transition-all ${
                tab === key ? "bg-bronze text-obsidian border-bronze" : "border-white/15 text-stone-400 hover:border-bronze/60 hover:text-cedar"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "enquiries" ? <Enquiries /> : <Projects />}
      </main>
    </div>
  );
}
